import { AnalyticsControllerService } from '../api/generated/requests/services.gen';
import { UserEventRequest } from '../api/generated/requests/types.gen';

// Session and device tracking utilities
interface SessionInfo {
  sessionId: string;
  sessionStartTime: string;
  sessionDuration: number;
  eventSequence: number;
}

interface DeviceInfo {
  userAgent: string;
  browser: {
    name: string;
    version: string;
  };
  os: {
    name: string;
    version: string;
  };
  device: {
    type: 'mobile' | 'tablet' | 'desktop';
    screenWidth: number;
    screenHeight: number;
    viewportWidth: number;
    viewportHeight: number;
    pixelRatio: number;
  };
  system: {
    language: string;
    languages: string[];
    timezone: string;
    cookieEnabled: boolean;
    onLine: boolean;
    hardwareConcurrency?: number;
    deviceMemory?: number;
  };
  connection?: {
    effectiveType?: string;
    downlink?: number;
    rtt?: number;
  };
}

// Source tracking interfaces
interface UTMParams {
  source?: string;      // utm_source
  medium?: string;      // utm_medium  
  campaign?: string;    // utm_campaign
  term?: string;        // utm_term
  content?: string;     // utm_content
}

enum SourceCategory {
  SOCIAL = "social",
  SEARCH = "search", 
  DIRECT = "direct",
  REFERRAL = "referral",
  EMAIL = "email",
  PAID = "paid",
  UNKNOWN = "unknown"
}

interface SourceInfo {
  platform: string;           // "instagram", "facebook", "google"
  category: SourceCategory;    // "social", "search", "direct", "referral"
  referrer?: string;          // original referrer URL
  utm?: UTMParams;            // parsed UTM parameters
  isFirstVisit: boolean;      // czy to pierwsze źródło w sesji
  detectedAt: string;         // timestamp when detected
}

interface AttributionInfo {
  firstTouch: string;         // first source/category
  lastTouch: string;          // current source/category
  touchCount: number;         // number of visits
}

// UTM Parameter Parser
class UTMParser {
  static parseURL(url: string): UTMParams {
    try {
      const urlObj = new URL(url);
      return {
        source: urlObj.searchParams.get('utm_source') || undefined,
        medium: urlObj.searchParams.get('utm_medium') || undefined,
        campaign: urlObj.searchParams.get('utm_campaign') || undefined,
        term: urlObj.searchParams.get('utm_term') || undefined,
        content: urlObj.searchParams.get('utm_content') || undefined,
      };
    } catch {
      return {};
    }
  }

  static parseCurrentURL(): UTMParams {
    return this.parseURL(window.location.href);
  }

  static hasUTMParams(utm: UTMParams): boolean {
    return !!(utm.source || utm.medium || utm.campaign || utm.term || utm.content);
  }
}

// Source Detection and Categorization
class SourceDetector {
  private static readonly SOCIAL_DOMAINS: Record<string, string> = {
    'instagram.com': 'instagram',
    'l.instagram.com': 'instagram',
    'facebook.com': 'facebook',
    'lm.facebook.com': 'facebook',
    'm.facebook.com': 'facebook',
    'twitter.com': 'twitter',
    't.co': 'twitter',
    'x.com': 'twitter',
    'linkedin.com': 'linkedin',
    'tiktok.com': 'tiktok',
    'youtube.com': 'youtube',
    'youtu.be': 'youtube',
    'pinterest.com': 'pinterest',
    'snapchat.com': 'snapchat',
    'whatsapp.com': 'whatsapp'
  };

  private static readonly SEARCH_DOMAINS: Record<string, string> = {
    'google.com': 'google',
    'google.pl': 'google',
    'bing.com': 'bing',
    'yahoo.com': 'yahoo',
    'duckduckgo.com': 'duckduckgo',
    'yandex.com': 'yandex',
    'baidu.com': 'baidu'
  };

  static detectSource(referrer: string, currentURL: string): SourceInfo {
    const utm = UTMParser.parseURL(currentURL);
    const timestamp = new Date().toISOString();
    
    // If no referrer, check for direct traffic or UTM
    if (!referrer || referrer === currentURL) {
      return this.handleDirectTraffic(utm, timestamp);
    }

    try {
      const referrerUrl = new URL(referrer);
      const domain = referrerUrl.hostname.toLowerCase().replace('www.', '');
      
      // Check for social media
      const socialPlatform = this.getSocialPlatform(domain);
      if (socialPlatform) {
        return {
          platform: socialPlatform,
          category: SourceCategory.SOCIAL,
          referrer,
          utm: UTMParser.hasUTMParams(utm) ? utm : undefined,
          isFirstVisit: this.isFirstVisit(),
          detectedAt: timestamp
        };
      }

      // Check for search engines
      const searchEngine = this.getSearchEngine(domain);
      if (searchEngine) {
        return {
          platform: searchEngine,
          category: SourceCategory.SEARCH,
          referrer,
          utm: UTMParser.hasUTMParams(utm) ? utm : undefined,
          isFirstVisit: this.isFirstVisit(),
          detectedAt: timestamp
        };
      }

      // Check UTM medium for categorization
      if (utm.medium) {
        const category = this.categorizeByUTM(utm);
        return {
          platform: utm.source || 'unknown',
          category,
          referrer,
          utm,
          isFirstVisit: this.isFirstVisit(),
          detectedAt: timestamp
        };
      }

      // Default to referral
      return {
        platform: domain,
        category: SourceCategory.REFERRAL,
        referrer,
        utm: UTMParser.hasUTMParams(utm) ? utm : undefined,
        isFirstVisit: this.isFirstVisit(),
        detectedAt: timestamp
      };

    } catch {
      // Invalid referrer URL, treat as direct
      return this.handleDirectTraffic(utm, timestamp);
    }
  }

  private static handleDirectTraffic(utm: UTMParams, timestamp: string): SourceInfo {
    if (UTMParser.hasUTMParams(utm)) {
      const category = utm.medium ? this.categorizeByUTM(utm) : SourceCategory.DIRECT;
      return {
        platform: utm.source || 'direct',
        category,
        utm,
        isFirstVisit: this.isFirstVisit(),
        detectedAt: timestamp
      };
    }

    return {
      platform: 'direct',
      category: SourceCategory.DIRECT,
      isFirstVisit: this.isFirstVisit(),
      detectedAt: timestamp
    };
  }

  private static getSocialPlatform(domain: string): string | null {
    for (const [socialDomain, platform] of Object.entries(this.SOCIAL_DOMAINS)) {
      if (domain.includes(socialDomain) || socialDomain.includes(domain)) {
        return platform;
      }
    }
    return null;
  }

  private static getSearchEngine(domain: string): string | null {
    for (const [searchDomain, engine] of Object.entries(this.SEARCH_DOMAINS)) {
      if (domain.includes(searchDomain) || searchDomain.includes(domain)) {
        return engine;
      }
    }
    return null;
  }

  private static categorizeByUTM(utm: UTMParams): SourceCategory {
    if (!utm.medium) return SourceCategory.UNKNOWN;

    const medium = utm.medium.toLowerCase();
    
    if (medium.includes('email')) return SourceCategory.EMAIL;
    if (medium.includes('social')) return SourceCategory.SOCIAL;
    if (medium.includes('search') || medium.includes('organic')) return SourceCategory.SEARCH;
    if (medium.includes('cpc') || medium.includes('ppc') || medium.includes('paid')) return SourceCategory.PAID;
    if (medium.includes('referral')) return SourceCategory.REFERRAL;
    
    return SourceCategory.UNKNOWN;
  }

  private static isFirstVisit(): boolean {
    // Check if there's any previous source stored
    return !localStorage.getItem('analytics_first_source');
  }
}

// Persistent Source Tracking
class SourceTracker {
  private static readonly FIRST_SOURCE_KEY = 'analytics_first_source';
  private static readonly SESSION_SOURCE_KEY = 'analytics_session_source';
  private static readonly ATTRIBUTION_KEY = 'analytics_attribution';

  static trackSource(sourceInfo: SourceInfo): { current: SourceInfo; first?: SourceInfo; attribution: AttributionInfo } {
    // Store current session source
    this.storeSessionSource(sourceInfo);
    
    // Get or set first touch source
    let firstSource = this.getFirstSource();
    if (!firstSource) {
      firstSource = sourceInfo;
      this.storeFirstSource(firstSource);
    }

    // Update attribution info
    const attribution = this.updateAttribution(firstSource, sourceInfo);
    
    return {
      current: sourceInfo,
      first: firstSource,
      attribution
    };
  }

  static getCurrentSource(): SourceInfo | null {
    try {
      const stored = sessionStorage.getItem(this.SESSION_SOURCE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }

  static getFirstSource(): SourceInfo | null {
    try {
      const stored = localStorage.getItem(this.FIRST_SOURCE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }

  static getAttribution(): AttributionInfo | null {
    try {
      const stored = localStorage.getItem(this.ATTRIBUTION_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }

  private static storeSessionSource(sourceInfo: SourceInfo): void {
    try {
      sessionStorage.setItem(this.SESSION_SOURCE_KEY, JSON.stringify(sourceInfo));
    } catch {
      // Handle storage errors silently
    }
  }

  private static storeFirstSource(sourceInfo: SourceInfo): void {
    try {
      localStorage.setItem(this.FIRST_SOURCE_KEY, JSON.stringify(sourceInfo));
    } catch {
      // Handle storage errors silently
    }
  }

  private static updateAttribution(firstSource: SourceInfo, currentSource: SourceInfo): AttributionInfo {
    let attribution = this.getAttribution();
    
    if (!attribution) {
      attribution = {
        firstTouch: `${firstSource.platform}/${firstSource.category}`,
        lastTouch: `${currentSource.platform}/${currentSource.category}`,
        touchCount: 1
      };
    } else {
      attribution.lastTouch = `${currentSource.platform}/${currentSource.category}`;
      
      // Only increment if it's a new session with different source
      const lastSource = this.getCurrentSource();
      if (!lastSource || 
          lastSource.platform !== currentSource.platform || 
          lastSource.category !== currentSource.category) {
        attribution.touchCount += 1;
      }
    }

    try {
      localStorage.setItem(this.ATTRIBUTION_KEY, JSON.stringify(attribution));
    } catch {
      // Handle storage errors silently
    }

    return attribution;
  }

  static detectAndTrackSource(): { current: SourceInfo; first?: SourceInfo; attribution: AttributionInfo } {
    const referrer = document.referrer;
    const currentURL = window.location.href;
    const sourceInfo = SourceDetector.detectSource(referrer, currentURL);
    
    return this.trackSource(sourceInfo);
  }

  static resetTracking(): void {
    try {
      localStorage.removeItem(this.FIRST_SOURCE_KEY);
      sessionStorage.removeItem(this.SESSION_SOURCE_KEY);
      localStorage.removeItem(this.ATTRIBUTION_KEY);
    } catch {
      // Handle storage errors silently
    }
  }
}

class SessionManager {
  private static readonly SESSION_KEY = 'analytics_session';
  private static readonly SEQUENCE_KEY = 'analytics_sequence';

  static getSessionInfo(): SessionInfo {
    let sessionData = this.getStoredSession();
    
    if (!sessionData) {
      sessionData = this.createNewSession();
      this.storeSession(sessionData);
    }

    // Update sequence number for this event
    const sequence = this.incrementSequence();
    const now = Date.now();
    const duration = now - new Date(sessionData.sessionStartTime).getTime();

    return {
      ...sessionData,
      sessionDuration: duration,
      eventSequence: sequence
    };
  }

  private static generateSessionId(): string {
    // Generate a unique session ID using crypto API if available, fallback to timestamp + random
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    
    // Fallback method
    return `session_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
  }

  private static createNewSession(): Omit<SessionInfo, 'sessionDuration' | 'eventSequence'> {
    return {
      sessionId: this.generateSessionId(),
      sessionStartTime: new Date().toISOString()
    };
  }

  private static getStoredSession(): Omit<SessionInfo, 'sessionDuration' | 'eventSequence'> | null {
    try {
      const stored = sessionStorage.getItem(this.SESSION_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }

  private static storeSession(sessionData: Omit<SessionInfo, 'sessionDuration' | 'eventSequence'>): void {
    try {
      sessionStorage.setItem(this.SESSION_KEY, JSON.stringify(sessionData));
    } catch {
      // Handle storage errors silently
    }
  }

  private static incrementSequence(): number {
    try {
      const current = parseInt(sessionStorage.getItem(this.SEQUENCE_KEY) || '0', 10);
      const next = current + 1;
      sessionStorage.setItem(this.SEQUENCE_KEY, next.toString());
      return next;
    } catch {
      return 1;
    }
  }
}

class DeviceInfoCollector {
  static collectDeviceInfo(): DeviceInfo {
    const userAgent = navigator.userAgent;
    const browser = this.parseBrowserInfo(userAgent);
    const os = this.parseOSInfo(userAgent);
    const device = this.collectDeviceDetails();
    const system = this.collectSystemInfo();
    const connection = this.collectConnectionInfo();

    return {
      userAgent,
      browser,
      os,
      device,
      system,
      ...(connection && { connection })
    };
  }

  private static parseBrowserInfo(userAgent: string): DeviceInfo['browser'] {
    // Simple browser detection - could be enhanced with a proper library
    let name = 'Unknown';
    let version = 'Unknown';

    if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) {
      name = 'Chrome';
      const match = userAgent.match(/Chrome\/([0-9.]+)/);
      version = match ? match[1] : version;
    } else if (userAgent.includes('Firefox')) {
      name = 'Firefox';
      const match = userAgent.match(/Firefox\/([0-9.]+)/);
      version = match ? match[1] : version;
    } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
      name = 'Safari';
      const match = userAgent.match(/Version\/([0-9.]+)/);
      version = match ? match[1] : version;
    } else if (userAgent.includes('Edg')) {
      name = 'Edge';
      const match = userAgent.match(/Edg\/([0-9.]+)/);
      version = match ? match[1] : version;
    }

    return { name, version };
  }

  private static parseOSInfo(userAgent: string): DeviceInfo['os'] {
    let name = 'Unknown';
    let version = 'Unknown';

    if (userAgent.includes('Windows')) {
      name = 'Windows';
      if (userAgent.includes('Windows NT 10.0')) version = '10/11';
      else if (userAgent.includes('Windows NT 6.3')) version = '8.1';
      else if (userAgent.includes('Windows NT 6.2')) version = '8';
      else if (userAgent.includes('Windows NT 6.1')) version = '7';
    } else if (userAgent.includes('Mac OS X')) {
      name = 'macOS';
      const match = userAgent.match(/Mac OS X ([0-9._]+)/);
      version = match ? match[1].replace(/_/g, '.') : version;
    } else if (userAgent.includes('Linux')) {
      name = 'Linux';
    } else if (userAgent.includes('Android')) {
      name = 'Android';
      const match = userAgent.match(/Android ([0-9.]+)/);
      version = match ? match[1] : version;
    } else if (userAgent.includes('iOS')) {
      name = 'iOS';
      const match = userAgent.match(/OS ([0-9_]+)/);
      version = match ? match[1].replace(/_/g, '.') : version;
    }

    return { name, version };
  }

  private static collectDeviceDetails(): DeviceInfo['device'] {
    const screenWidth = window.screen.width;
    const screenHeight = window.screen.height;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const pixelRatio = window.devicePixelRatio || 1;

    // Simple device type detection based on screen size and user agent
    let type: 'mobile' | 'tablet' | 'desktop' = 'desktop';
    const userAgent = navigator.userAgent.toLowerCase();
    
    if (/mobile|android|iphone/.test(userAgent) && Math.min(screenWidth, screenHeight) < 768) {
      type = 'mobile';
    } else if (/tablet|ipad/.test(userAgent) || (Math.min(screenWidth, screenHeight) >= 768 && Math.min(screenWidth, screenHeight) < 1024)) {
      type = 'tablet';
    }

    return {
      type,
      screenWidth,
      screenHeight,
      viewportWidth,
      viewportHeight,
      pixelRatio
    };
  }

  private static collectSystemInfo(): DeviceInfo['system'] {
    const language = navigator.language;
    const languages = navigator.languages ? Array.from(navigator.languages) : [language];
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const cookieEnabled = navigator.cookieEnabled;
    const onLine = navigator.onLine;

    // Optional hardware info (may not be available in all browsers)
    const hardwareConcurrency = (navigator as any).hardwareConcurrency;
    const deviceMemory = (navigator as any).deviceMemory;

    return {
      language,
      languages,
      timezone,
      cookieEnabled,
      onLine,
      ...(hardwareConcurrency && { hardwareConcurrency }),
      ...(deviceMemory && { deviceMemory })
    };
  }

  private static collectConnectionInfo(): DeviceInfo['connection'] | undefined {
    // Network Information API (experimental, not widely supported)
    const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
    
    if (connection) {
      return {
        effectiveType: connection.effectiveType,
        downlink: connection.downlink,
        rtt: connection.rtt
      };
    }

    return undefined;
  }
}

/**
 * Analytics service for tracking user events
 */
export class AnalyticsService {
  private static deviceInfo: DeviceInfo | null = null;

  /**
   * Get cached device info or collect it if not cached
   */
  private static getDeviceInfo(): DeviceInfo {
    if (!this.deviceInfo) {
      this.deviceInfo = DeviceInfoCollector.collectDeviceInfo();
    }
    return this.deviceInfo;
  }

  /**
   * Track a user event with automatic session and device information
   * @param eventName - The name of the event to track
   * @param additionalData - Optional additional data to include with the event
   */
  static async trackEvent(eventName: string, additionalData?: Record<string, any>): Promise<void> {
    try {
      // Get current page information
      const origin = window.location.origin;
      const referer = document.referrer || window.location.href;

      // Collect session and device information
      const sessionInfo = SessionManager.getSessionInfo();
      const deviceInfo = this.getDeviceInfo();
      
      // Collect source tracking information
      const sourceTracking = SourceTracker.detectAndTrackSource();

      // Combine all event data
      const eventData = {
        event_type: eventName,
        timestamp: new Date().toISOString(),
        session: sessionInfo,
        device: deviceInfo,
        page: {
          url: window.location.href,
          title: document.title,
          origin: origin,
          referer: referer,
          pathname: window.location.pathname,
          search: window.location.search,
          hash: window.location.hash
        },
        source: {
          current: sourceTracking.current,
          first: sourceTracking.first,
          attribution: sourceTracking.attribution
        },
        ...additionalData
      };

      // Create the event request body
      const requestBody: UserEventRequest = {
        event: JSON.stringify(eventData)
      };

      // Send the analytics event
      await AnalyticsControllerService.postAnalytics({
        origin,
        referer,
        requestBody
      });

      console.log(`Analytics event tracked: ${eventName}`, {
        sessionId: sessionInfo.sessionId,
        eventSequence: sessionInfo.eventSequence,
        deviceType: deviceInfo.device.type,
        browser: `${deviceInfo.browser.name} ${deviceInfo.browser.version}`,
        additionalData
      });
    } catch (error) {
      // Log error but don't throw to avoid interrupting user flow
      console.error('Failed to track analytics event:', eventName, error);
    }
  }

  /**
   * Track login button click event
   * @param formData - Optional form data to include (password will be excluded)
   */
  static async trackLoginClick(formData?: { email?: string }): Promise<void> {
    const eventData: Record<string, any> = {
      timestamp: new Date().toISOString(),
      page: 'login',
      action: 'button_click'
    };

    // Add form data if provided (excluding sensitive fields)
    if (formData) {
      if (formData.email) {
        eventData.form_data = {
          email: formData.email
        };
      }
    }

    await this.trackEvent('login_click', eventData);
  }

  /**
   * Track registration button click event
   * @param formData - Optional form data to include (password fields will be excluded)
   */
  static async trackRegistrationClick(formData?: { 
    login?: string; 
    firstName?: string; 
    lastName?: string; 
  }): Promise<void> {
    const eventData: Record<string, any> = {
      timestamp: new Date().toISOString(),
      page: 'registration',
      action: 'button_click'
    };

    // Add form data if provided (excluding sensitive fields like passwords)
    if (formData) {
      const allowedFields: Record<string, any> = {};
      
      if (formData.login) allowedFields.login = formData.login;
      if (formData.firstName) allowedFields.firstName = formData.firstName;
      if (formData.lastName) allowedFields.lastName = formData.lastName;

      if (Object.keys(allowedFields).length > 0) {
        eventData.form_data = allowedFields;
      }
    }

    await this.trackEvent('registration_click', eventData);
  }
}